import AsyncOperationToast from '@/components/AsyncOperationToast.vue';
import { getErrorTk } from '@/utils/error.utils';
import { h } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast, ToastType, type Id } from 'vue3-toastify';
import { DEFAULT_AUTOCLOSE } from '@/plugins/toastify';

export interface IToastAction {
  label: string;
  onClick: () => Promise<void> | void;
}

export interface IActionToastBehavior {
  autoClose: number | false;
  closeButton: boolean;
  closeOnClick: boolean;
  hideProgressBar: boolean;
  pauseOnFocusLoss?: boolean;
  pauseOnHover?: boolean;
  bodyClassName: string;
}

interface IUpsertActionToastParams {
  message: string;
  type: 'success' | 'error';
  action?: IToastAction;
  behavior: IActionToastBehavior;
}

interface IActionSuccessToastParams {
  message: string;
  action: IToastAction;
}

interface IToastComposable {
  actionSuccessToast: (params: IActionSuccessToastParams) => void;
  createSuccessToast: (createdAmount?: number) => void;
  customSuccessToast: (message: string) => void;
  customErrorToast: (message: string) => void;
  editSuccessToast: () => void;
  deleteSuccessToast: () => void;
  showError: (...args: Parameters<typeof getErrorTk>) => void;
  catchError<T>(cb: () => Promise<T>, overrides?: Parameters<typeof getErrorTk>[1]): Promise<T>;
}

interface IUniqueToastParams {
  key: string;
  message: string;
  type: ToastType;
}

const ACTION_SUCCESS_TOAST_BEHAVIOR: IActionToastBehavior = {
  autoClose: 8000,
  closeButton: false,
  closeOnClick: false,
  hideProgressBar: true,
  bodyClassName: 'toast-body--rich',
};

// Shows a toast with an optional action button, updating in place when the toastId is already
// active. Shared by useAsyncOperations' job toasts and actionSuccessToast below.
export const upsertActionToast = (toastId: Id, params: IUpsertActionToastParams): void => {
  const action = params.action
    ? {
        label: params.action.label,
        onClick: async () => {
          toast.remove(toastId);
          await params.action?.onClick();
        },
      }
    : undefined;

  const render = h(AsyncOperationToast, {
    message: params.message,
    action,
    onClose: () => toast.remove(toastId),
  });

  if (!toast.isActive(toastId)) {
    if (params.type === 'success') {
      toast.success(render, { ...params.behavior, toastId });
    } else {
      toast.error(render, { ...params.behavior, toastId });
    }
    return;
  }

  toast.update(toastId, {
    ...params.behavior,
    isLoading: false,
    render,
    type: params.type,
  });
};

export const useToast = (): IToastComposable => {
  const { t } = useI18n();

  // Prevents Toast from stacking if a new toast with the same key + message is triggered.
  const uniqueToast = (params: IUniqueToastParams): void => {
    const { key, message, type } = params;
    const id = `${key}:${message}`;
    const toastOptions = {
      toastId: id,
      autoClose: DEFAULT_AUTOCLOSE,
    };

    // Update existing Toast if active instead of stacking new one
    if (toast.isActive(id)) {
      toast.update(id, {
        autoClose: DEFAULT_AUTOCLOSE,
      });
      return;
    }

    // Show toast if no active instance with same key + message
    switch (type) {
      case 'success':
        toast.success(message, toastOptions);
        break;
      case 'error':
        toast.error(message, toastOptions);
        break;
      default:
        // No default behaviour. Can be extended with different ToastTypes if ever needed.
        break;
    }
  };

  const catchError = async <T>(cb: () => Promise<T>, overrides?: Parameters<typeof getErrorTk>[1]): Promise<T> => {
    try {
      return await cb();
    } catch (e) {
      showError(e, overrides);
      throw new Error(`unexpected catch: ${e}`);
    }
  };

  // Success toast with an action button. Re-triggering with the same
  // message swaps the render so the action targets the latest result.
  const actionSuccessToast = (params: IActionSuccessToastParams): void => {
    upsertActionToast(`action-success:${params.message}`, {
      message: params.message,
      type: 'success',
      action: params.action,
      behavior: ACTION_SUCCESS_TOAST_BEHAVIOR,
    });
  };

  const createSuccessToast = (createdAmount?: number): void => {
    uniqueToast({
      key: 'create-success',
      message: t('general.createSuccess', { count: createdAmount ?? 1 }),
      type: 'success',
    });
  };

  const customSuccessToast = (message: string): void => {
    uniqueToast({ key: 'custom-success', message, type: 'success' });
  };

  const customErrorToast = (message: string): void => {
    uniqueToast({ key: 'custom-error', message, type: 'error' });
  };

  const deleteSuccessToast = (): void => {
    uniqueToast({ key: 'delete-success', message: t('general.deleteSuccess'), type: 'success' });
  };

  const editSuccessToast = (): void => {
    uniqueToast({ key: 'edit-success', message: t('general.editSuccess'), type: 'success' });
  };

  const showError: (...args: Parameters<typeof getErrorTk>) => void = (error, fallbackTk) => {
    console.error('Error', error);
    uniqueToast({ key: 'error', message: t(getErrorTk(error, fallbackTk)), type: 'error' });
  };

  return {
    actionSuccessToast,
    catchError,
    createSuccessToast,
    customErrorToast,
    customSuccessToast,
    deleteSuccessToast,
    editSuccessToast,
    showError,
  };
};
